// LT-354 (LT-325 C7): per-(contact, campaign) enrolment list panel.
// Replaces the Enrolments placeholder on CampaignDetailPage.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';

import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Pill,
  Select,
  type ColumnDef,
  type PillIntent,
} from '../../ui';
import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import CampaignEnrolContactModal from './CampaignEnrolContactModal';

export type CampaignEnrolmentExitStatus =
  | 'qualified'
  | 'opted_out'
  | 'nurture_routed'
  | 'callback_requested'
  | 'removed'
  | 'campaign_force_ended'
  | 'completed';

export interface CampaignEnrolment {
  id: string;
  workspace_id: string;
  campaign_id: string;
  contact_id: string;
  /** Display name resolved server-side from Contact (full name → email →
   * phone). Falls back to truncated UUID if all are absent. */
  contact_name?: string | null;
  contact_email?: string | null;
  joined_at: string;
  // Backend returns the exact string; we render via STATUS_INTENT lookup
  // so unknown values fall back to neutral rather than failing the type.
  exit_status: string | null;
  exited_at: string | null;
  exit_reason: string | null;
  deal_id: string | null;
  manual_override_actor_user_id: string | null;
  manual_override_at: string | null;
  post_completion_campaign_id: string | null;
  created_at: string;
  updated_at: string | null;
}

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'opted_out', label: 'Opted out' },
  { value: 'nurture_routed', label: 'Nurture routed' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'removed', label: 'Removed' },
  { value: 'campaign_force_ended', label: 'Force-ended' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_INTENT: Record<string, PillIntent> = {
  qualified: 'ok',
  opted_out: 'danger',
  nurture_routed: 'info',
  callback_requested: 'warn',
  removed: 'neutral',
  campaign_force_ended: 'danger',
  completed: 'neutral',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

interface CampaignEnrolmentsPanelProps {
  campaignId: string;
}

const CampaignEnrolmentsPanel: React.FC<CampaignEnrolmentsPanelProps> = ({
  campaignId,
}) => {
  const [rows, setRows] = useState<CampaignEnrolment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showEnrolModal, setShowEnrolModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    campaignsAPI
      .enrolments(campaignId, statusFilter || undefined)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractCampaignApiError(err, 'Failed to load enrolments.'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, statusFilter, refreshKey]);

  const columns = useMemo<ColumnDef<CampaignEnrolment>[]>(
    () => [
      {
        id: 'contact_id',
        header: 'Contact',
        accessor: (e) => {
          const label = e.contact_name || e.contact_email || e.contact_id.slice(0, 8);
          return (
            <Link
              to={`/contacts/${e.contact_id}`}
              className="text-accent hover:text-accent-hover hover:underline text-xs"
            >
              {label}
            </Link>
          );
        },
      },
      {
        id: 'joined_at',
        header: 'Joined',
        sortable: true,
        sortValue: (e) => new Date(e.joined_at).getTime(),
        accessor: (e) => (
          <span className="text-slate-400">{formatDate(e.joined_at)}</span>
        ),
      },
      {
        id: 'exit_status',
        header: 'Status',
        accessor: (e) =>
          e.exit_status ? (
            <Pill intent={STATUS_INTENT[e.exit_status] ?? 'neutral'}>
              {e.exit_status.replace(/_/g, ' ')}
            </Pill>
          ) : (
            <Pill intent="accent">active</Pill>
          ),
      },
      {
        id: 'exit_reason',
        header: 'Reason',
        accessor: (e) =>
          e.exit_reason ? (
            <span className="text-slate-300 text-xs">{e.exit_reason}</span>
          ) : (
            <span className="text-slate-600">—</span>
          ),
      },
      {
        id: 'deal_id',
        header: 'Deal',
        accessor: (e) =>
          e.deal_id ? (
            <Link
              to={`/deals/${e.deal_id}`}
              className="text-accent hover:text-accent-hover hover:underline font-mono text-xs"
            >
              {e.deal_id.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-slate-600">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <Card>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-200">Enrolments</h3>
          <p className="text-xs text-slate-500">
            Per-contact enrolment with exit status + linked deal. Reporting
            dashboard reads this same table.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter enrolments by status"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || '_all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button intent="primary" onClick={() => setShowEnrolModal(true)}>
            Enrol contact
          </Button>
        </div>
      </div>

      {showEnrolModal ? (
        <CampaignEnrolContactModal
          campaignId={campaignId}
          onClose={() => setShowEnrolModal(false)}
          onEnrolled={() => {
            setShowEnrolModal(false);
            setRefreshKey((n) => n + 1);
          }}
        />
      ) : null}

      {error ? (
        <div className="mb-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-xs">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-slate-500 px-3 py-6">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          surface="campaign_enrolments"
          icon={Users}
          headline={
            statusFilter
              ? `No enrolments with status "${statusFilter}"`
              : 'No enrolments yet'
          }
          subhead={
            statusFilter
              ? 'Try a different filter or clear it to see everything.'
              : 'Enrolments land here once contacts are added to this campaign.'
          }
          primary={{
            label: 'Clear filter',
            onClick: () => setStatusFilter(''),
          }}
        />
      ) : (
        <DataTable<CampaignEnrolment>
          data={rows}
          columns={columns}
          getRowKey={(e) => e.id}
          ariaLabel="Campaign enrolments"
        />
      )}
    </Card>
  );
};

export default CampaignEnrolmentsPanel;
